import { useRef, useState, useLayoutEffect, type PointerEvent } from 'react';
import type { Point } from '../lib/document-scan-geometry';

interface Props {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  corners: Point[];
  onChange: (corners: Point[]) => void;
}

/** Zeigt ein Foto mit 4 ziehbaren Eckpunkten (Overlay) — `corners` kommen/gehen in
 *  ORIGINAL-Bildkoordinaten (imageWidth/imageHeight); intern wird auf die tatsächliche
 *  Anzeigegröße (responsive) umgerechnet. */
export default function CornerOverlay({ imageUrl, imageWidth, imageHeight, corners, onChange }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setDisplaySize({ width: rect.width, height: rect.height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toDisplay = (p: Point): Point => ({
    x: displaySize.width ? (p.x / imageWidth) * displaySize.width : 0,
    y: displaySize.height ? (p.y / imageHeight) * displaySize.height : 0,
  });
  const toNatural = (x: number, y: number): Point => ({
    x: displaySize.width ? (x / displaySize.width) * imageWidth : 0,
    y: displaySize.height ? (y / displaySize.height) * imageHeight : 0,
  });

  const handlePointerDown = (index: number) => (e: PointerEvent) => {
    e.preventDefault();
    const target = e.target as Element;
    if (typeof target.setPointerCapture === 'function') target.setPointerCapture(e.pointerId);
    setDragIndex(index);
  };

  const handlePointerMove = (e: PointerEvent) => {
    if (dragIndex === null || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
    const y = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);
    const next = [...corners];
    next[dragIndex] = toNatural(x, y);
    onChange(next);
  };

  const handlePointerUp = () => setDragIndex(null);

  const polygonPoints = corners
    .map((c) => {
      const d = toDisplay(c);
      return `${d.x},${d.y}`;
    })
    .join(' ');

  return (
    <div
      ref={containerRef}
      className="relative w-full select-none touch-none"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <img src={imageUrl} alt="Foto" className="w-full rounded-lg block" draggable={false} />
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <polygon points={polygonPoints} fill="rgba(37, 99, 235, 0.2)" stroke="rgb(37, 99, 235)" strokeWidth={2} />
      </svg>
      {corners.map((c, i) => {
        const d = toDisplay(c);
        return (
          <div
            key={i}
            data-testid={`corner-${i}`}
            onPointerDown={handlePointerDown(i)}
            className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full bg-blue-600 border-2 border-white shadow-md touch-none"
            style={{ left: d.x, top: d.y }}
          />
        );
      })}
    </div>
  );
}
